/**
 * Safely open a popup window and load the given HTML without using
 * `document.write` (which is flagged as an XSS/eval sink by linters,
 * even when we fully control the HTML).
 *
 * We use a Blob + object URL so the browser treats the content as a
 * same-origin HTML document and we never touch `document.write`.
 *
 * @param {string} html - Full HTML document (`<!doctype html>...`).
 * @param {object} [opts]
 * @param {string} [opts.features] - window.open features string.
 * @param {boolean} [opts.autoPrint=true] - call window.print() once loaded.
 * @returns {Window|null} the popup window, or null if blocked.
 */
export function openPrintWindow(html, { features = "width=800,height=900", autoPrint = true } = {}) {
  const blob = new Blob([html], { type: "text/html;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const w = window.open(url, "_blank", features);
  if (!w) {
    URL.revokeObjectURL(url);
    return null;
  }
  // Revoke the URL once the popup has loaded, but keep a small grace period
  // so the browser definitely has the content before the URL is gone.
  const cleanup = () => {
    try { URL.revokeObjectURL(url); } catch (err) { console.debug("printWindow: URL already revoked", err); }
  };
  if (autoPrint) {
    const onLoad = () => {
      try { w.focus(); w.print(); } catch (err) { console.debug("printWindow: popup closed before print", err); }
      setTimeout(cleanup, 2000);
    };
    // "load" may not fire if blob is cached quickly — use both load and timeout.
    try { w.addEventListener("load", onLoad, { once: true }); } catch (err) { console.debug("printWindow: cross-origin addEventListener", err); }
    setTimeout(onLoad, 300);
  } else {
    setTimeout(cleanup, 5000);
  }
  return w;
}
